import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle, XCircle } from 'lucide-react';

interface StatusToggleConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  driverName: string;
  driverId: number;
  isActive: boolean;
  onConfirm: (driverId: number, remarks: string) => void;
  isLoading?: boolean;
}

const StatusToggleConfirmation: React.FC<StatusToggleConfirmationProps> = ({
  open,
  onOpenChange,
  driverName,
  driverId,
  isActive,
  onConfirm,
  isLoading = false,
}) => {
  const [remarks, setRemarks] = useState('');

  const handleConfirm = () => {
    onConfirm(driverId, remarks);
    setRemarks(''); // Reset remarks after confirmation
  };

  const handleCancel = () => {
    setRemarks(''); // Reset remarks when canceling
    onOpenChange(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setRemarks(''); // Reset remarks when dialog closes
    }
    onOpenChange(newOpen);
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            {isActive ? (
              <XCircle className="h-5 w-5 text-warning" />
            ) : (
              <CheckCircle className="h-5 w-5 text-success" />
            )}
            <AlertDialogTitle>
              {isActive ? 'Deactivate Driver' : 'Activate Driver'}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left">
            Are you sure you want to {isActive ? 'deactivate' : 'activate'}{' '}
            <span className="font-semibold">{driverName}</span>?
            {isActive && (
              <div className="mt-2 p-3 bg-warning/10 border border-warning/20 rounded-md">
                <p className="text-sm text-warning font-medium">
                  <strong>Warning:</strong> Deactivating this driver will prevent them from
                  accepting new rides.
                </p>
              </div>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Label htmlFor="remarks" className="text-sm font-medium">
            Remarks <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="remarks"
            placeholder="Enter reason for status change..."
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            className="mt-2"
            required
          />
          {remarks.trim().length === 0 && (
            <p className="text-xs text-destructive mt-1">Remarks are required</p>
          )}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading} onClick={handleCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading || remarks.trim().length === 0}
            className={isActive ? 'bg-warning hover:bg-warning/90' : 'bg-success hover:bg-success/90'}
          >
            {isLoading ? 'Processing...' : isActive ? 'Deactivate' : 'Activate'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default StatusToggleConfirmation;
